import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-[hsl(var(--overlay)/var(--overlay-alpha))] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * DialogContent - FIXED for proper overflow handling
 * - Modal is positioned fixed and centered
 * - Content STAYS WITHIN modal bounds
 * - Internal scrolling for overflow content
 * - NO overflow:visible (that causes spill-out)
 */
const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Positioning: fixed, centered
        "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
        // Sizing: responsive width with max constraints
        "w-[95vw] max-w-4xl",
        // Height: constrained to viewport
        "max-h-[90vh]",
        // Layout: flex column to manage header/body/footer
        "flex flex-col",
        // Styling
        "border border-line bg-[hsl(var(--dialog))] text-content shadow-overlay sm:rounded-card",
        // CRITICAL: overflow hidden on outer, scroll on inner body
        "overflow-hidden",
        // Animation
        "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="dialog-close-target absolute right-4 top-4 -m-3 flex items-center justify-center w-10 h-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

/**
 * DialogHeader - Fixed header at top of modal
 */
const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex-shrink-0 px-6 pt-6 pb-4",
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

/**
 * DialogBody - Scrollable content area
 * This is the key fix: body scrolls, content stays within bounds
 */
const DialogBody = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex-1 overflow-auto px-6",
      "min-h-0", // Critical for flex scroll to work
      className
    )}
    {...props} />
)
DialogBody.displayName = "DialogBody"

/**
 * DialogFooter - Fixed footer at bottom
 */
const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex-shrink-0 px-6 pb-6 pt-4",
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
